import { useEffect, useState } from "react";
import { googleMapsSearchUrl } from "@japangolmok/content";
import {
	preparationDue,
	tripStage,
	expenseJpy,
	type TripWorkspace,
} from "@japangolmok/core";
import { listTrips } from "../storage/trips";
import { ExternalLink, Layout, usePageMeta } from "../ui/Shell";
import { track } from "../analytics/events";
import { BottomSheet } from "../ui/BottomSheet";
import { TripCreatePage } from "./TripPages";
import {
	JourneySummary,
	journeySummaryFromTrip,
	sampleSummary,
} from "./JourneySummary";

export const travelToday = () =>
	new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Tokyo" }).format(
		new Date(),
	);

export function chooseCurrentTrip(
	trips: TripWorkspace[],
	today = travelToday(),
) {
	const active = trips.filter(
		(trip) => !["completed", "archived"].includes(tripStage(trip, today)),
	);
	return (
		active.sort(
			(a, b) =>
				Number(tripStage(b, today) === "traveling") -
					Number(tripStage(a, today) === "traveling") ||
				a.startDate.localeCompare(b.startDate) ||
				b.updatedAt.localeCompare(a.updatedAt),
		)[0] ?? null
	);
}

function useLocalTrips() {
	const [trips, setTrips] = useState<TripWorkspace[] | null>(null);
	const [failed, setFailed] = useState(false);
	const load = () => {
		setFailed(false);
		void listTrips()
			.then(setTrips)
			.catch(() => setFailed(true));
	};
	useEffect(load, []);
	return { trips, failed, load };
}

export function TravelHome() {
	const [createOpen, setCreateOpen] = useState(false);
	const { trips, failed, load } = useLocalTrips();
	const [online, setOnline] = useState(
		() => typeof navigator === "undefined" || navigator.onLine !== false,
	);
	useEffect(() => {
		const sync = () => setOnline(navigator.onLine);
		window.addEventListener("online", sync);
		window.addEventListener("offline", sync);
		return () => {
			window.removeEventListener("online", sync);
			window.removeEventListener("offline", sync);
		};
	}, []);
	const today = travelToday();
	const current =
		chooseCurrentTrip(trips ?? []) ??
		[...(trips ?? [])]
			.filter((t) => tripStage(t, today) === "completed")
			.sort((a, b) => b.endDate.localeCompare(a.endDate))[0] ??
		null;
	const stage = current ? tripStage(current, today) : null;
	const todayPlan = current?.days.find((d) => d.date === today);
	const next = todayPlan?.items.find((item) => !item.completed);
	const due = current
		? preparationDue(current, today)
				.filter((item) => item.withinWeek)
				.slice(0, 3)
		: [];
	const summary = current ? journeySummaryFromTrip(current) : null;
	const todayExpenses =
		current?.expenses.filter((item) => item.occurredOn === today) ?? [];
	const todaySpent = todayExpenses.reduce(
		(sum, item) => sum + (expenseJpy(item, current?.krwPer100Jpy ?? null) ?? 0),
		0,
	);
	const todayUnconverted = todayExpenses.filter(
		(item) => expenseJpy(item, current?.krwPer100Jpy ?? null) === null,
	).length;

	usePageMeta(
		"일본골목 — 일행과 보는 여행 한 장",
		"일정과 일행, 여행 돈을 한곳에 모으고 현지에서도 꺼내요.",
	);
	useEffect(() => {
		const query = new URLSearchParams(window.location.search);
		track("landing_view", {
			surface: query.has("utm_source") ? "campaign" : "direct",
		});
	}, []);
	return (
		<Layout>
			<div className="journey-home">
				<h1>일본 여행 일정·일행·쓴 돈을 한 장에</h1>
				<p>공항·날짜 → 3일 일정 → 일행에게 보내기</p>
				{failed ? (
					<aside role="alert">
						<p>
							여행 목록을 읽지 못했어요. 저장한 여행이 없다는 뜻은 아니에요.
						</p>
						<button className="button secondary" onClick={load}>
							다시 읽기
						</button>
					</aside>
				) : trips === null ? (
					<p role="status">여행을 불러오는 중이에요…</p>
				) : current ? (
					<article className="journey-sheet">
						<div className="journey-sheet__heading">
							<div>
								<span>
									{stage === "traveling"
										? "지금 여행 중"
										: stage === "completed"
											? "다녀온 여행"
											: "출발 준비"}
								</span>
								<h2>{current.title}</h2>
								<p>
									{current.origin} → {current.destinationCity} ·{" "}
									{current.startDate} ~ {current.endDate}
								</p>
							</div>
							<a href={`/trips/${encodeURIComponent(current.id)}`}>
								전체 보기 →
							</a>
						</div>
						{stage === "traveling" && (
							<section className="journey-next">
								<span>다음 장소</span>
								<h3>{next?.title ?? "오늘 남은 일정이 없어요"}</h3>
								{next?.time && <p>{next.time} · 직접 적은 시각</p>}
								{next &&
									(online ? (
										<p>
											<ExternalLink
												href={googleMapsSearchUrl(next.mapQuery ?? next.title)}
											>
												지도 앱으로 열기
											</ExternalLink>
										</p>
									) : (
										<p>
											지금은 장소 이름을 보여 주세요. 지도는 연결되면 열려요.
										</p>
									))}
								<a href={`/trips/${encodeURIComponent(current.id)}/today`}>
									오늘 일정 열기 →
								</a>
							</section>
						)}
						{stage !== "traveling" && stage !== "completed" && (
							<section>
								<h3>이번 주 할 일</h3>
								{due.length ? (
									<ul>
										{due.map((item) => (
											<li key={item.id}>
												{item.title} · {item.due}
											</li>
										))}
									</ul>
								) : (
									<p>이번 주에 확인할 준비가 없어요.</p>
								)}
							</section>
						)}
						{stage === "traveling" && (
							<p>
								오늘 쓴 돈 ¥{todaySpent.toLocaleString()}
								{todayUnconverted > 0 &&
									` · 환율 없는 지출 ${todayUnconverted}건 별도`}
							</p>
						)}
						{summary && (
							<JourneySummary
								summary={summary}
								budgetHref={`/trips/${encodeURIComponent(current.id)}#budget`}
							/>
						)}
						<a
							className="button primary"
							href={`/trips/${encodeURIComponent(current.id)}`}
						>
							내 여행 열기
						</a>{" "}
						{stage !== "completed" && (
							<a href={`/trips/${encodeURIComponent(current.id)}#calendar`}>
								출발 알림 넣기 →
							</a>
						)}
					</article>
				) : (
					<article
						className="journey-sheet journey-sheet--sample"
						aria-label="예시 여행 한 장"
					>
						<span className="journey-sample-label">
							예시 여행 · 저장되지 않아요
						</span>
						<h2>후쿠오카 2박 3일</h2>
						<p>부산 PUS → 후쿠오카 FUK</p>
						<JourneySummary summary={sampleSummary} />
					</article>
				)}
				<p className="journey-move">
					<a href="/guides/move-trip">트리플 일정·가계부 옮겨오기 →</a>
				</p>
				<a
					className={`button ${current ? "secondary" : "primary journey-create"}`}
					href="/trips/new"
					onClick={(event) => { event.preventDefault(); setCreateOpen(true); }}
				>
					내 여행 만들기
				</a>
				<nav className="home-tools" aria-label="다른 여행 도구">
					<a href="/flights">출발공항별 일본 직항</a>
					<a href="/walks">동네와 코스</a>
					<a href="/guides/packing">여행 준비물</a>
					<a href="/trips">저장한 여행</a>
					<a href="/cards">공항 첫 길</a>
					<a href="/companions">한 코스 함께 걷기</a>
				</nav>
			</div>
			{createOpen && <BottomSheet title="내 여행 만들기" onClose={() => setCreateOpen(false)}><TripCreatePage embedded /></BottomSheet>}
		</Layout>
	);
}

export function TodayLandingPage() {
	const { trips, failed, load } = useLocalTrips();
	const current = chooseCurrentTrip(trips ?? []);
	usePageMeta(
		"오늘의 일본 여행 — 일본골목",
		"저장한 여행의 다음 장소와 길찾기, 남은 일정 변경을 바로 열어요.",
	);
	return (
		<Layout>
			<section className="today-landing">
				<span className="eyebrow">오늘 필요한 것부터</span>
				<h1>
					지금의 여행을
					<br />
					바로 열어요.
				</h1>
				{failed ? (
					<div role="alert">
						<p>
							여행 저장소를 읽지 못했어요. 브라우저의 사이트 데이터 허용과 저장 공간을 확인하고, 다른 일본골목 탭을 닫은 뒤 다시 읽어 주세요. 원래 기록은 지우지 않아요.
						</p>
						<button className="button secondary" type="button" onClick={load}>
							다시 읽기
						</button>
					</div>
				) : trips === null ? (
					<p role="status">이 기기의 여행을 읽고 있어요…</p>
				) : current ? (
					<article className="today-current">
						<span>
							{current.destinationCity} · {current.startDate}–{current.endDate}
						</span>
						<h2>{current.title}</h2>
						<a
							className="button primary"
							href={`/trips/${encodeURIComponent(current.id)}/today`}
						>
							{tripStage(current, travelToday()) === "traveling"
								? "오늘 일정 열기"
								: "여행 첫날 미리 보기"}{" "}
							→
						</a>
						<a href={`/trips/${encodeURIComponent(current.id)}#itinerary`}>
							전체 일정 수정하기
						</a>
					</article>
				) : (
					<div className="today-no-trip">
						<p>
							아직 다가오는 여행이 없어요. 날짜를 정하지 않았어도 동네부터 고를
							수 있어요.
						</p>
						<a className="button primary" href="/trips/new">내 여행 만들기 →</a>
						<article className="today-current" aria-label="저장되지 않는 오늘 견본">
							<span>견본 · 저장되지 않아요</span><h2>후쿠오카에서 보내는 하루</h2>
							<p>10:00 오호리공원 · 12:00 하카타에서 점심</p>
							<p>오늘 일정과 지출, 필요한 길을 한곳에서 볼 수 있어요.</p>
						</article>
						<a href="/walks">지금 가고 싶은 동네 고르기 →</a>
						<a className="button secondary" href="/trips">
							다녀온 여행과 백업 보기
						</a>
					</div>
				)}
				{trips && trips.length > 1 && (
					<details>
						<summary>다른 여행을 열고 싶어요</summary>
						<ul>
							{trips
								.filter((trip) => trip.id !== current?.id)
								.map((trip) => (
									<li key={trip.id}>
										<a href={`/trips/${encodeURIComponent(trip.id)}/today`}>
											{trip.title} · {trip.startDate}
										</a>
									</li>
								))}
						</ul>
					</details>
				)}
				<div className="home-tools">
					<a href="/cards">공항 첫 이동 카드</a>
					<a href="/companions">한 끼·한 코스 동행</a>
				</div>
			</section>
		</Layout>
	);
}
